import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type {
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ApartmentRounded,
  ChevronRightRounded,
  DragIndicatorRounded,
  ExpandMoreRounded,
  PersonRounded,
  SearchRounded,
  SupervisorAccountRounded,
} from '@mui/icons-material';

import type {
  StacOrgEditorUnit,
  StacOrgEditorWorker,
} from '../types/stac';


export type StacOrgSelection =
  | {
    kind: 'unit';
    id: string;
  }
  | {
    kind: 'worker';
    id: string;
  };


type ActiveDrag =
  | {
    kind: 'unit';
    unitId: string;
  }
  | {
    kind: 'worker';
    workerId: string;
  };


interface PendingUnitMove {
  unitId: string;
  targetUnitId: string;
}


interface StacOrganizationTreeWorkspaceProps {
  rootUnit: StacOrgEditorUnit;
  legacyUnits: StacOrgEditorUnit[];

  unitById: Map<
    string,
    StacOrgEditorUnit
  >;

  workerById: Map<
    string,
    StacOrgEditorWorker
  >;

  childrenByParent: Map<
    string,
    StacOrgEditorUnit[]
  >;

  workersByUnit: Map<
    string,
    StacOrgEditorWorker[]
  >;

  expanded: Set<string>;
  selection: StacOrgSelection | null;

  search: string;
  trayWorkers: StacOrgEditorWorker[];
  unassignedWorkerCount: number;

  busy: boolean;

  onSearchChange: (
    value: string
  ) => void;

  onLocateWorker: (
    worker: StacOrgEditorWorker
  ) => void;

  onSelect: (
    selection: StacOrgSelection
  ) => void;

  onToggle: (
    unitId: string
  ) => void;

  onExpandAll: () => void;
  onCollapseAll: () => void;

  onMoveWorker: (
    workerId: string,
    targetUnitId: string | null,
  ) => void;

  onMoveUnit: (
    unitId: string,
    targetUnitId: string,
  ) => void;
}


function descendantsOf(
  unitId: string,
  childrenByParent: Map<
    string,
    StacOrgEditorUnit[]
  >,
): Set<string> {
  const result =
    new Set<string>();

  const visit = (
    currentId: string,
  ) => {
    for (
      const child
      of childrenByParent.get(
        currentId
      )
      ?? []
    ) {
      if (
        result.has(
          child.id
        )
      ) {
        continue;
      }

      result.add(
        child.id
      );

      visit(
        child.id
      );
    }
  };

  visit(
    unitId
  );

  return result;
}


function WorkerRow({
  worker,
  isManager,
  selected,
  busy,
  onSelect,
}: {
  worker: StacOrgEditorWorker;
  isManager: boolean;
  selected: boolean;
  busy: boolean;
  onSelect: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    id:
      `stac-worker:${worker.worker_id}`,
    data: {
      kind: 'worker',
      workerId:
        worker.worker_id,
    } satisfies ActiveDrag,
    disabled: busy,
  });

  return (
    <Paper
      ref={setNodeRef}
      elevation={0}
      onClick={onSelect}
      sx={{
        borderRadius: 2.25,
        border:
          selected
            ? (
              '1px solid '
              + 'rgba(56,189,248,0.55)'
            )
            : (
              '1px solid '
              + 'rgba(148,163,184,0.09)'
            ),
        bgcolor:
          selected
            ? 'rgba(56,189,248,0.09)'
            : 'rgba(15,23,42,0.42)',
        opacity:
          isDragging
            ? 0.35
            : 1,
        transition:
          'border-color 140ms ease, '
          + 'background-color 140ms ease, '
          + 'opacity 140ms ease',
        px: 1,
        py: 0.7,
        cursor: 'pointer',
      }}
    >
      <Stack
        direction="row"
        spacing={0.8}
        alignItems="center"
      >
        <Tooltip
          title="Arrastrar persona"
        >
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            onClick={(event) =>
              event.stopPropagation()
            }
            sx={{
              width: 26,
              height: 26,
              cursor:
                busy
                  ? 'default'
                  : 'grab',
              '&:active': {
                cursor:
                  busy
                    ? 'default'
                    : 'grabbing',
              },
            }}
          >
            <DragIndicatorRounded
              sx={{
                fontSize: 17,
                color: '#64748b',
              }}
            />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor:
              isManager
                ? 'rgba(56,189,248,0.13)'
                : 'rgba(148,163,184,0.08)',
            border:
              isManager
                ? (
                  '1px solid '
                  + 'rgba(56,189,248,0.28)'
                )
                : (
                  '1px solid '
                  + 'rgba(148,163,184,0.12)'
                ),
            flexShrink: 0,
          }}
        >
          <PersonRounded
            sx={{
              fontSize: 17,
              color:
                isManager
                  ? '#7dd3fc'
                  : '#94a3b8',
            }}
          />
        </Box>

        <Typography
          noWrap
          sx={{
            flex: 1,
            minWidth: 0,
            color: '#f8fafc',
            fontWeight:
              isManager
                ? 900
                : 750,
            fontSize: '0.82rem',
          }}
        >
          {worker.display_name}
        </Typography>

        {isManager ? (
          <Chip
            size="small"
            icon={
              <SupervisorAccountRounded />
            }
            label="Responsable"
            variant="outlined"
            sx={{
              color: '#7dd3fc',
              '& .MuiChip-icon': {
                color: '#7dd3fc',
              },
            }}
          />
        ) : null}
      </Stack>
    </Paper>
  );
}


function UnitCard({
  unit,
  children,
  directWorkers,
  isOpen,
  selected,
  busy,
  activeDrag,
  invalidTargetUnitIds,
  onSelect,
  onToggle,
}: {
  unit: StacOrgEditorUnit;
  children: StacOrgEditorUnit[];
  directWorkers: StacOrgEditorWorker[];
  isOpen: boolean;
  selected: boolean;
  busy: boolean;
  activeDrag: ActiveDrag | null;
  invalidTargetUnitIds: Set<string>;
  onSelect: () => void;
  onToggle: () => void;
}) {
  const canAcceptDrop =
    !busy
    && !invalidTargetUnitIds.has(
      unit.id
    );

  const {
    setNodeRef:
      setDroppableRef,
    isOver,
  } = useDroppable({
    id:
      `stac-unit-drop:${unit.id}`,
    data: {
      kind: 'unit',
      unitId: unit.id,
    },
    disabled:
      !canAcceptDrop,
  });

  const {
    attributes,
    listeners,
    setNodeRef:
      setDraggableRef,
    isDragging,
  } = useDraggable({
    id:
      `stac-unit:${unit.id}`,
    data: {
      kind: 'unit',
      unitId: unit.id,
    } satisfies ActiveDrag,
    disabled:
      busy
      || unit.is_root,
  });

  const hasContent =
    children.length > 0
    || directWorkers.length > 0;

  const dropLabel =
    activeDrag?.kind
      === 'worker'
      ? (
        unit.is_root
          ? 'Dejar sin ubicar'
          : 'Ubicar aquí'
      )
      : 'Mover aquí';

  return (
    <Paper
      ref={(element) => {
        setDroppableRef(
          element
        );

        setDraggableRef(
          element
        );
      }}
      elevation={0}
      onClick={onSelect}
      sx={{
        cursor: 'pointer',
        borderRadius: 2.6,
        border:
          isOver
          && canAcceptDrop
            ? (
              '1px solid '
              + 'rgba(34,211,238,0.95)'
            )
            : selected
              ? (
                '1px solid '
                + 'rgba(56,189,248,0.65)'
              )
              : (
                '1px solid '
                + 'rgba(148,163,184,0.13)'
              ),
        bgcolor:
          isOver
          && canAcceptDrop
            ? 'rgba(34,211,238,0.10)'
            : selected
              ? 'rgba(14,165,233,0.08)'
              : 'rgba(2,6,23,0.30)',
        boxShadow:
          isOver
          && canAcceptDrop
            ? (
              '0 0 0 3px '
              + 'rgba(34,211,238,0.08)'
            )
            : 'none',
        opacity:
          isDragging
            ? 0.35
            : 1,
        transition:
          'border-color 140ms ease, '
          + 'background-color 140ms ease, '
          + 'box-shadow 140ms ease, '
          + 'opacity 140ms ease',
        px: 1,
        py: 0.9,
      }}
    >
      <Stack
        direction="row"
        spacing={0.8}
        alignItems="center"
      >
        <IconButton
          size="small"
          disabled={!hasContent}
          onClick={(event) => {
            event.stopPropagation();

            onToggle();
          }}
          sx={{
            width: 28,
            height: 28,
          }}
        >
          {hasContent ? (
            isOpen ? (
              <ExpandMoreRounded />
            ) : (
              <ChevronRightRounded />
            )
          ) : null}
        </IconButton>

        <Box
          sx={{
            width: 31,
            height: 31,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor:
              unit.is_root
                ? 'rgba(56,189,248,0.14)'
                : 'rgba(56,189,248,0.08)',
            border:
              '1px solid '
              + 'rgba(56,189,248,0.18)',
            flexShrink: 0,
          }}
        >
          <ApartmentRounded
            sx={{
              color: '#38bdf8',
              fontSize: 18,
            }}
          />
        </Box>

        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={0.7}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <Typography
              noWrap
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              {unit.name}
            </Typography>

            {isOver
            && canAcceptDrop ? (
              <Chip
                size="small"
                label={dropLabel}
                sx={{
                  color: '#67e8f9',
                  borderColor:
                    'rgba(34,211,238,0.32)',
                  bgcolor:
                    'rgba(34,211,238,0.08)',
                }}
                variant="outlined"
              />
            ) : null}
          </Stack>

          <Stack
            direction="row"
            spacing={0.7}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            {unit
              .manager_display_name ? (
              <Typography
                sx={{
                  color: '#7dd3fc',
                  fontSize: '0.72rem',
                }}
              >
                Resp.
                {' '}
                {
                  unit
                    .manager_display_name
                }
              </Typography>
            ) : null}

            {!unit.is_root ? (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.72rem',
                }}
              >
                {
                  unit
                    .direct_worker_count
                }
                {' '}
                pers. ·
                {' '}
                {
                  unit
                    .child_unit_count
                }
                {' '}
                sub.
              </Typography>
            ) : (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.72rem',
                }}
              >
                Raíz organizativa
              </Typography>
            )}
          </Stack>
        </Box>

        {!unit.is_root ? (
          <Tooltip
            title="Arrastrar unidad"
          >
            <IconButton
              size="small"
              {...attributes}
              {...listeners}
              onClick={(event) =>
                event.stopPropagation()
              }
              sx={{
                width: 28,
                height: 28,
                cursor:
                  busy
                    ? 'default'
                    : 'grab',
                '&:active': {
                  cursor:
                    busy
                      ? 'default'
                      : 'grabbing',
                },
              }}
            >
              <DragIndicatorRounded
                sx={{
                  color: '#64748b',
                  fontSize: 18,
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Paper>
  );
}


export default function StacOrganizationTreeWorkspace({
  rootUnit,
  legacyUnits,
  unitById,
  workerById,
  childrenByParent,
  workersByUnit,
  expanded,
  selection,
  search,
  trayWorkers,
  unassignedWorkerCount,
  busy,
  onSearchChange,
  onLocateWorker,
  onSelect,
  onToggle,
  onExpandAll,
  onCollapseAll,
  onMoveWorker,
  onMoveUnit,
}: StacOrganizationTreeWorkspaceProps) {
  const sensors = useSensors(
    useSensor(
      PointerSensor,
      {
        activationConstraint: {
          distance: 6,
        },
      },
    ),
  );

  const [
    activeDrag,
    setActiveDrag,
  ] = useState<ActiveDrag | null>(
    null
  );

  const [
    pendingUnitMove,
    setPendingUnitMove,
  ] = useState<
    PendingUnitMove | null
  >(
    null
  );

  const invalidTargetUnitIds =
    useMemo(() => {
      if (
        activeDrag?.kind
        !== 'unit'
      ) {
        return new Set<string>();
      }

      const descendants =
        descendantsOf(
          activeDrag.unitId,
          childrenByParent,
        );

      descendants.add(
        activeDrag.unitId
      );

      return descendants;
    }, [
      activeDrag,
      childrenByParent,
    ]);

  const activeWorker =
    activeDrag?.kind === 'worker'
      ? workerById.get(
        activeDrag.workerId
      )
        ?? null
      : null;

  const activeUnit =
    activeDrag?.kind === 'unit'
      ? unitById.get(
        activeDrag.unitId
      )
        ?? null
      : null;

  const handleDragStart = (
    event: DragStartEvent,
  ) => {
    const data =
      event.active.data.current;

    if (
      data?.kind === 'worker'
      && typeof data.workerId
        === 'string'
    ) {
      setActiveDrag({
        kind: 'worker',
        workerId:
          data.workerId,
      });

      return;
    }

    if (
      data?.kind === 'unit'
      && typeof data.unitId
        === 'string'
    ) {
      setActiveDrag({
        kind: 'unit',
        unitId:
          data.unitId,
      });
    }
  };

  const handleDragEnd = (
    event: DragEndEvent,
  ) => {
    const currentDrag =
      activeDrag;

    setActiveDrag(
      null
    );

    const targetUnitId =
      event.over?.data.current
        ?.unitId;

    if (
      !currentDrag
      || typeof targetUnitId
        !== 'string'
    ) {
      return;
    }

    const targetUnit =
      unitById.get(
        targetUnitId
      );

    if (!targetUnit) {
      return;
    }

    if (
      currentDrag.kind
      === 'worker'
    ) {
      const worker =
        workerById.get(
          currentDrag.workerId
        );

      if (!worker) {
        return;
      }

      const resolvedTarget =
        targetUnit.is_root
          ? null
          : targetUnit.id;

      const currentTarget =
        worker.is_unassigned
          ? null
          : worker.unit_id;

      if (
        currentTarget
        === resolvedTarget
      ) {
        return;
      }

      onMoveWorker(
        worker.worker_id,
        resolvedTarget,
      );

      return;
    }

    if (
      invalidTargetUnitIds.has(
        targetUnit.id
      )
    ) {
      return;
    }

    const unit =
      unitById.get(
        currentDrag.unitId
      );

    if (
      !unit
      || unit.is_root
      || unit.parent_unit_id
        === targetUnit.id
    ) {
      return;
    }

    setPendingUnitMove({
      unitId: unit.id,
      targetUnitId:
        targetUnit.id,
    });
  };

  const renderUnit = (
    unit: StacOrgEditorUnit,
    depth = 0,
  ): ReactNode => {
    const children =
      childrenByParent.get(
        unit.id
      )
      ?? [];

    const directWorkers =
      workersByUnit.get(
        unit.id
      )
      ?? [];

    const isOpen =
      expanded.has(
        unit.id
      );

    const selected =
      selection?.kind
        === 'unit'
      && selection.id
        === unit.id;

    return (
      <Box
        key={unit.id}
        sx={{
          ml:
            depth
              ? 1.55
              : 0,
        }}
      >
        <UnitCard
          unit={unit}
          children={children}
          directWorkers={
            directWorkers
          }
          isOpen={isOpen}
          selected={selected}
          busy={busy}
          activeDrag={
            activeDrag
          }
          invalidTargetUnitIds={
            invalidTargetUnitIds
          }
          onSelect={() =>
            onSelect({
              kind: 'unit',
              id: unit.id,
            })
          }
          onToggle={() =>
            onToggle(
              unit.id
            )
          }
        />

        {isOpen ? (
          <Stack
            spacing={0.65}
            sx={{
              mt: 0.65,
              ml: 1.15,
              pl: 1.1,
              borderLeft:
                '1px solid '
                + 'rgba(148,163,184,0.14)',
            }}
          >
            {directWorkers.map(
              (worker) => (
                <WorkerRow
                  key={
                    worker.worker_id
                  }
                  worker={worker}
                  isManager={
                    unit
                      .manager_worker_id
                    === worker
                      .worker_id
                  }
                  selected={
                    selection?.kind
                      === 'worker'
                    && selection.id
                      === worker
                        .worker_id
                  }
                  busy={busy}
                  onSelect={() =>
                    onSelect({
                      kind:
                        'worker',
                      id:
                        worker
                          .worker_id,
                    })
                  }
                />
              ),
            )}

            {children.map(
              (child) =>
                renderUnit(
                  child,
                  depth + 1,
                ),
            )}
          </Stack>
        ) : null}
      </Box>
    );
  };

  const pendingUnit =
    pendingUnitMove
      ? unitById.get(
        pendingUnitMove.unitId
      )
        ?? null
      : null;

  const pendingTarget =
    pendingUnitMove
      ? unitById.get(
        pendingUnitMove
          .targetUnitId
      )
        ?? null
      : null;

  const normalizedSearch =
    search
      .trim()
      .toLocaleLowerCase('es');

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={
          closestCenter
        }
        onDragStart={
          handleDragStart
        }
        onDragCancel={() =>
          setActiveDrag(
            null
          )
        }
        onDragEnd={
          handleDragEnd
        }
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg:
                'minmax(220px,.55fr) '
                + 'minmax(440px,1.45fr)',
            },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border:
                '1px solid '
                + 'rgba(148,163,184,0.16)',
              bgcolor:
                'rgba(15,23,42,0.72)',
              p: 1.5,
              minHeight: 560,
            }}
          >
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              Personas
            </Typography>

            <Typography
              sx={{
                mt: 0.2,
                color: '#64748b',
                fontSize: '0.78rem',
              }}
            >
              Arrastra personas sin ubicar
              hacia una unidad. Busca por
              nombre para localizar cualquiera.
            </Typography>

            <TextField
              size="small"
              placeholder="Buscar persona"
              value={search}
              onChange={(event) =>
                onSearchChange(
                  event.target.value
                )
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                  >
                    <SearchRounded
                      sx={{
                        fontSize: 18,
                        color: '#64748b',
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              fullWidth
              sx={{
                mt: 1.2,
              }}
            />

            <Stack
              direction="row"
              spacing={0.7}
              sx={{
                mt: 1,
              }}
            >
              <Chip
                size="small"
                label={
                  normalizedSearch
                    ? (
                      `${trayWorkers.length} ${
                        trayWorkers.length
                          === 1
                          ? 'resultado'
                          : 'resultados'
                      }`
                    )
                    : (
                      `${unassignedWorkerCount} sin ubicar`
                    )
                }
                variant="outlined"
                sx={{
                  color:
                    !normalizedSearch
                    && unassignedWorkerCount
                      ? '#fbbf24'
                      : undefined,
                }}
              />
            </Stack>

            <Divider
              sx={{
                my: 1.2,
              }}
            />

            {trayWorkers.length ? (
              <Stack
                spacing={0.65}
                sx={{
                  maxHeight: 460,
                  overflowY: 'auto',
                }}
              >
                {trayWorkers.map(
                  (worker) => {
                    const unit =
                      worker.unit_id
                        ? unitById.get(
                          worker.unit_id
                        )
                          ?? null
                        : null;

                    const isManager =
                      Boolean(
                        unit
                        && unit
                          .manager_worker_id
                          === worker
                            .worker_id
                      );

                    return (
                      <Box
                        key={
                          worker.worker_id
                        }
                      >
                        <WorkerRow
                          worker={worker}
                          isManager={
                            isManager
                          }
                          selected={
                            selection?.kind
                              === 'worker'
                            && selection.id
                              === worker
                                .worker_id
                          }
                          busy={busy}
                          onSelect={() =>
                            onLocateWorker(
                              worker
                            )
                          }
                        />

                        <Typography
                          sx={{
                            mt: 0.25,
                            ml: 4.5,
                            color:
                              worker
                                .has_legacy_placement
                                ? '#fbbf24'
                                : worker
                                    .is_unassigned
                                  ? '#fbbf24'
                                  : '#64748b',
                            fontSize:
                              '0.7rem',
                          }}
                        >
                          {worker
                            .has_legacy_placement
                            ? 'Pendiente de normalizar'
                            : worker
                                .is_unassigned
                              ? 'Sin ubicar'
                              : (
                                unit?.name
                                ?? 'Ubicada'
                              )}
                        </Typography>
                      </Box>
                    );
                  },
                )}
              </Stack>
            ) : normalizedSearch ? (
              <Alert
                severity="info"
                variant="outlined"
              >
                No se encontraron personas
                con ese nombre.
              </Alert>
            ) : (
              <Alert
                severity="success"
                variant="outlined"
              >
                Todas las personas están
                ubicadas en el organigrama.
              </Alert>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border:
                '1px solid '
                + 'rgba(148,163,184,0.16)',
              bgcolor:
                'rgba(15,23,42,0.72)',
              p: 1.5,
              minHeight: 560,
            }}
          >
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              justifyContent=
                "space-between"
              alignItems={{
                xs: 'stretch',
                sm: 'center',
              }}
              spacing={1}
            >
              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  Organigrama
                </Typography>

                <Typography
                  sx={{
                    mt: 0.2,
                    color: '#64748b',
                    fontSize: '0.78rem',
                  }}
                >
                  Arrastra personas para
                  cambiar su unidad y unidades
                  para reorganizar ramas.
                </Typography>
              </Box>

              <Stack
                direction="row"
                spacing={0.5}
              >
                <Button
                  size="small"
                  variant="text"
                  onClick={onExpandAll}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Expandir todo
                </Button>

                <Button
                  size="small"
                  variant="text"
                  onClick={onCollapseAll}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Contraer
                </Button>
              </Stack>
            </Stack>

            <Box
              sx={{
                mt: 1.2,
                p: 1,
                borderRadius: 2.5,
                bgcolor:
                  'rgba(2,6,23,0.25)',
                border:
                  '1px solid '
                  + 'rgba(148,163,184,0.08)',
              }}
            >
              {renderUnit(
                rootUnit
              )}
            </Box>

            {legacyUnits.length ? (
              <Stack
                spacing={0.7}
                sx={{
                  mt: 1.5,
                }}
              >
                <Typography
                  sx={{
                    color: '#fbbf24',
                    fontWeight: 900,
                    fontSize: '0.78rem',
                  }}
                >
                  Pendiente de normalizar
                </Typography>

                {legacyUnits.map(
                  (unit) => (
                    <Paper
                      key={unit.id}
                      elevation={0}
                      onClick={() =>
                        onSelect({
                          kind: 'unit',
                          id: unit.id,
                        })
                      }
                      sx={{
                        cursor: 'pointer',
                        p: 1,
                        borderRadius: 2,
                        border:
                          '1px solid '
                          + 'rgba(245,158,11,0.25)',
                        bgcolor:
                          selection?.kind
                            === 'unit'
                          && selection.id
                            === unit.id
                            ? 'rgba(245,158,11,0.10)'
                            : 'rgba(245,158,11,0.04)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 850,
                        }}
                      >
                        {unit.name}
                      </Typography>

                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.72rem',
                        }}
                      >
                        Bajo
                        {' '}
                        {
                          unit
                            .legacy_parent_display_name
                          ?? 'relación heredada'
                        }
                      </Typography>
                    </Paper>
                  ),
                )}
              </Stack>
            ) : null}
          </Paper>
        </Box>

        <DragOverlay>
          {activeWorker ? (
            <Paper
              elevation={8}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2.5,
                bgcolor: '#172033',
                border:
                  '1px solid '
                  + 'rgba(56,189,248,0.40)',
                minWidth: 220,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <PersonRounded
                  sx={{
                    color: '#38bdf8',
                  }}
                />

                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 900,
                  }}
                >
                  {
                    activeWorker
                      .display_name
                  }
                </Typography>
              </Stack>
            </Paper>
          ) : activeUnit ? (
            <Paper
              elevation={8}
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 2.5,
                bgcolor: '#172033',
                border:
                  '1px solid '
                  + 'rgba(56,189,248,0.40)',
                minWidth: 220,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <ApartmentRounded
                  sx={{
                    color: '#38bdf8',
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    {
                      activeUnit
                        .name
                    }
                  </Typography>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.75rem',
                    }}
                  >
                    Mover rama
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Dialog
        open={
          Boolean(
            pendingUnitMove
          )
        }
        onClose={() =>
          setPendingUnitMove(
            null
          )
        }
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#0f172a',
            backgroundImage:
              'none',
            color: '#f8fafc',
          },
        }}
      >
        <DialogTitle>
          Mover unidad
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5}>
            <Typography
              sx={{
                color: '#cbd5e1',
              }}
            >
              Vas a mover
              {' '}
              <strong>
                {
                  pendingUnit
                    ?.name
                }
              </strong>
              {' '}
              y toda su rama dentro de
              {' '}
              <strong>
                {
                  pendingTarget
                    ?.name
                }
              </strong>
              .
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.8rem',
              }}
            >
              La posición anterior quedará
              conservada en el histórico.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() =>
              setPendingUnitMove(
                null
              )
            }
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={
              !pendingUnitMove
              || busy
            }
            onClick={() => {
              if (
                !pendingUnitMove
              ) {
                return;
              }

              onMoveUnit(
                pendingUnitMove
                  .unitId,
                pendingUnitMove
                  .targetUnitId,
              );

              setPendingUnitMove(
                null
              );
            }}
          >
            Mover rama
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
